import { categories, mainSiteUrl, faqs, articles } from "@/mocks/categories";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = typeof import.meta !== "undefined" && (import.meta as Record<string, unknown>).env
  ? ((import.meta as Record<string, unknown>).env as Record<string, string>).VITE_SITE_URL || "https://mulebuyspreadsheets.makeup"
  : "https://mulebuyspreadsheets.makeup";

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Mulebuy Spreadsheet",
  "url": siteUrl,
  "description": "Your ultimate guide to China product sourcing and spreadsheet shopping. Discover trending products, agent catalogs, and the best mulebuy spreadsheet deals.",
  "inLanguage": "en",
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer,
    },
  })),
};

export default function Home() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleArticleClick = (articleId: string) => {
    window.scrollTo({ top: 0, behavior: "instant" });
    navigate(`/${articleId}`);
  };

  return (
    <div className="min-h-screen bg-background-50 font-sans">
      <JsonLd data={websiteSchema as unknown as Record<string, unknown>} />
      <JsonLd data={faqSchema as unknown as Record<string, unknown>} />
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white shadow-sm border-b border-stone-200/70"
            : "bg-white border-b border-stone-200/50"
        }`}
      >
        <div className="w-full px-4 md:px-8 flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-xl font-bold text-stone-800 hover:text-primary-600 transition-colors whitespace-nowrap">
            Mulebuy Spreadsheet
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-semibold text-stone-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Home
            </Link>
            <Link
              to="/mulebuy-spreadsheet-shopping-guide"
              className="px-4 py-2 text-sm font-semibold text-stone-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Shopping Guide
            </Link>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap text-sm font-semibold"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-list-check text-sm"></i>
                </span>
                Categories
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </span>
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden z-50 max-h-80 overflow-y-auto">
                  {categories.map((cat, i) => (
                    <a
                      key={i}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-stone-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap text-sm font-semibold"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-stone-700"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-200 shadow-lg max-h-[70vh] overflow-y-auto">
            <div className="px-4 py-3 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 rounded-lg transition-colors"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                  <i className="ri-home-line text-sm"></i>
                </span>
                Home
              </Link>
              <Link
                to="/mulebuy-spreadsheet-shopping-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 rounded-lg transition-colors"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                  <i className="ri-book-open-line text-sm"></i>
                </span>
                Shopping Guide
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-accent-600 hover:bg-accent-50 rounded-lg transition-colors"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                  <i className="ri-shopping-bag-line text-sm"></i>
                </span>
                Buy It Now
              </a>
              <div className="border-t border-stone-100 pt-3 mt-3">
                <p className="px-4 py-2 text-xs font-semibold text-stone-400 uppercase tracking-wider">Categories</p>
                {categories.map((cat, i) => (
                  <a
                    key={i}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-stone-600 hover:bg-stone-50 rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 min-h-[600px] md:min-h-[700px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Organized%20flat%20lay%20of%20diverse%20fashion%20products%20sneakers%20bags%20hoodies%20accessories%20arranged%20in%20clean%20grid%20pattern%20on%20warm%20cream%20marble%20surface%20minimal%20editorial%20product%20photography%20soft%20natural%20diffused%20lighting%20sophisticated%20aesthetic&width=1600&height=900&seq=hero-mulebuy-v2&orientation=landscape"
            alt="Mulebuy Spreadsheet Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/70 via-stone-900/50 to-stone-900/70"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 py-16 md:py-24 text-center">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Mulebuy Spreadsheet
            <br />
            <span className="text-primary-400">The Ultimate China Product Sourcing System</span>
          </h1>
          <p className="text-lg md:text-xl text-stone-200 max-w-2xl mx-auto mb-10 leading-relaxed">
            Discover the most complete <strong>mulebuy spreadsheet</strong> database for trending products, agent shopping lists, and China sourcing guides.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-4 bg-primary-500 text-white rounded-xl font-semibold text-lg hover:bg-primary-600 transition-all whitespace-nowrap shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              Mulebuy Spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-4 bg-accent-500 text-white rounded-xl font-semibold text-lg hover:bg-accent-600 transition-all whitespace-nowrap shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </section>

      {/* Category Cards with Images */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-center text-stone-800 mb-4">
            Browse Product Categories
          </h2>
          <p className="text-center text-stone-500 mb-12 max-w-xl mx-auto">
            Explore our curated product categories and find exactly what you need from our mulebuy spreadsheet collection.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {categories.map((cat, i) => (
              <a
                key={i}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative rounded-xl overflow-hidden bg-stone-100 aspect-square cursor-pointer"
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-stone-900/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <span className="block text-white text-sm font-semibold leading-tight">
                    {cat.name}
                  </span>
                  <span className="flex items-center gap-1 text-primary-300 text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Shop Now
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-center text-stone-800 mb-12">
            Why Choose Mulebuy Spreadsheet?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon: "ri-eye-line", title: "Hidden Products", desc: "Access China products not found on Amazon or other mainstream platforms." },
              { icon: "ri-money-dollar-circle-line", title: "Factory Direct", desc: "Get cheaper factory-direct pricing without middlemen markup." },
              { icon: "ri-fire-line", title: "Trending Early", desc: "Discover viral TikTok items and trending products before they blow up." },
              { icon: "ri-bar-chart-line", title: "Easy Compare", desc: "Compare agent pricing systems and product specs side by side." },
            ].map((item, i) => (
              <div key={i} className="p-6 bg-stone-50 rounded-2xl border border-stone-100 hover:border-primary-200 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl mb-4">
                  <i className={`${item.icon} text-xl text-primary-600`}></i>
                </div>
                <h3 className="font-semibold text-stone-800 mb-2">{item.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-center text-stone-800 mb-4">
            Mulebuy Spreadsheet vs Other Platforms
          </h2>
          <p className="text-center text-stone-500 mb-12 max-w-xl mx-auto">
            See how our mulebuy spreadsheet system compares to traditional shopping methods.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-100 border-b border-stone-200">
                  <th className="px-4 md:px-6 py-4 text-left font-semibold text-stone-700">Platform</th>
                  <th className="px-4 md:px-6 py-4 text-center font-semibold text-stone-700">Product Access</th>
                  <th className="px-4 md:px-6 py-4 text-center font-semibold text-stone-700">Price</th>
                  <th className="px-4 md:px-6 py-4 text-center font-semibold text-stone-700">Ease</th>
                  <th className="px-4 md:px-6 py-4 text-center font-semibold text-stone-700">Risk</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { platform: "Mulebuy Spreadsheet", access: "Very High", price: "Low", ease: "Medium", risk: "Medium", highlight: true },
                  { platform: "Taobao Direct", access: "High", price: "Medium", ease: "Medium", risk: "Medium", highlight: false },
                  { platform: "Amazon", access: "Low", price: "High", ease: "Very Easy", risk: "Low", highlight: false },
                ].map((row, i) => (
                  <tr key={i} className={`border-b border-stone-100 ${row.highlight ? "bg-primary-50" : ""}`}>
                    <td className={`px-4 md:px-6 py-4 font-medium ${row.highlight ? "text-primary-700" : "text-stone-800"}`}>{row.platform}</td>
                    <td className="px-4 md:px-6 py-4 text-center text-stone-600">{row.access}</td>
                    <td className="px-4 md:px-6 py-4 text-center text-stone-600">{row.price}</td>
                    <td className="px-4 md:px-6 py-4 text-center text-stone-600">{row.ease}</td>
                    <td className="px-4 md:px-6 py-4 text-center text-stone-600">{row.risk}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* What is Mulebuy Spreadsheet */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-800 mb-6">
                What is Mulebuy Spreadsheet?
              </h2>
              <p className="text-stone-600 mb-4 leading-relaxed">
                <strong>Mulebuy spreadsheet</strong> is a curated product sourcing system designed for buyers who want to access China agent catalogs in a structured, organized format. It contains comprehensive product lists, pricing, and agent contact information all in one place.
              </p>
              <p className="text-stone-600 mb-6 leading-relaxed">
                This system is widely used by resellers, fashion enthusiasts, and bargain hunters to discover hidden gems from Chinese markets that are not available on mainstream platforms like Amazon or eBay.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap text-center"
                >
                  Explore Mulebuy Spreadsheet
                </a>
                <button
                  onClick={() => handleArticleClick("what-is-mulebuy-spreadsheet")}
                  className="px-6 py-3 bg-white border-2 border-primary-500 text-primary-700 rounded-lg font-semibold text-sm hover:bg-primary-50 transition-colors whitespace-nowrap text-center cursor-pointer"
                >
                  Read Full Guide
                </button>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20clean%20laptop%20on%20wooden%20desk%20displaying%20organized%20spreadsheet%20data%20with%20product%20thumbnails%20warm%20cream%20background%20soft%20natural%20daylight%20modern%20home%20office%20aesthetic%20editorial%20photography&width=800&height=600&seq=what-is-v2&orientation=landscape"
                alt="Mulebuy spreadsheet on laptop"
                className="w-full h-auto object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 9 SEO Article Cards */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-center text-stone-800 mb-4">
            Mulebuy Spreadsheet Guides & Articles
          </h2>
          <p className="text-center text-stone-500 mb-12 max-w-xl mx-auto">
            In-depth SEO articles to help you master the mulebuy spreadsheet system and become a spreadsheet shopping expert.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articles.map((article, i) => (
              <button
                key={i}
                onClick={() => handleArticleClick(article.id)}
                className="group bg-white rounded-2xl overflow-hidden border border-stone-200 hover:border-primary-300 hover:shadow-md transition-all text-left cursor-pointer w-full"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-stone-800 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-stone-500 leading-relaxed mb-4">
                    {article.desc}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                    Read Article
                    <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-center text-stone-800 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-stone-500 mb-12 max-w-xl mx-auto">
            Got questions about mulebuy spreadsheet? We have answers.
          </p>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-stone-200 rounded-xl overflow-hidden"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-stone-50 transition-colors"
                >
                  <span className="font-semibold text-stone-800 text-sm md:text-base" itemProp="name">{faq.question}</span>
                  <span className="w-6 h-6 flex items-center justify-center text-stone-400 flex-shrink-0 ml-3">
                    <i className={`ri-arrow-down-s-line transition-transform ${openFaq === i ? "rotate-180" : ""}`}></i>
                  </span>
                </button>
                {openFaq === i && (
                  <div
                    className="px-5 pb-4 text-stone-600 text-sm leading-relaxed"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <div itemProp="text">{faq.answer}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-primary-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-800 mb-4">
            Ready to Start Your Mulebuy Spreadsheet Journey?
          </h2>
          <p className="text-stone-600 mb-8 max-w-xl mx-auto leading-relaxed">
            Join thousands of buyers who use mulebuy spreadsheet to discover hidden products, save money, and stay ahead of trends.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-4 bg-primary-500 text-white rounded-xl font-semibold text-lg hover:bg-primary-600 transition-all whitespace-nowrap shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              Mulebuy Spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-4 bg-accent-500 text-white rounded-xl font-semibold text-lg hover:bg-accent-600 transition-all whitespace-nowrap shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-100 py-10 px-4 md:px-8 border-t border-stone-200">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-heading text-lg font-bold text-stone-800 mb-1">Mulebuy Spreadsheet</h3>
              <p className="text-sm text-stone-500">
                Your trusted guide to China product sourcing and spreadsheet shopping.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-stone-600 hover:text-primary-600 transition-colors">
                Main Store
              </a>
              <Link to="/" className="text-sm text-stone-600 hover:text-primary-600 transition-colors">
                Home
              </Link>
              <Link to="/what-is-mulebuy-spreadsheet" className="text-sm text-stone-600 hover:text-primary-600 transition-colors">
                Guide
              </Link>
              <Link to="/mulebuy-spreadsheet-shopping-guide" className="text-sm text-stone-600 hover:text-primary-600 transition-colors">
                Shopping Guide
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-stone-200 text-center">
            <p className="text-xs text-stone-400">
              &copy; 2026 Mulebuy Spreadsheet. All rights reserved. This is an informational site. All purchases are handled through third-party agents.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}